import type { HeaderFooter } from './header-footer';
import type { PatternLanguage } from './pattern-language';

/** Paper sizes a pattern can be laid out on. */
export type PaperSize = 'letter' | 'a4';

/** Page box in CSS px (96 per inch): Letter is 8.5 x 11 in, A4 is 210 x 297 mm. */
export const PAPER_DIMENSIONS: Record<PaperSize, { width: number; height: number }> = {
    letter: { width: 816, height: 1056 },
    a4: { width: 794, height: 1123 },
};

/** The `@page { size }` keyword for each paper size. */
export const CSS_PAGE_SIZE: Record<PaperSize, string> = {
    letter: 'letter',
    a4: 'A4',
};

/** Page margins in CSS px. */
export type Margins = { top: number; right: number; bottom: number; left: number };

/** 0.5 in on every side. Copy it (`{ ...DEFAULT_MARGINS }`) before storing it in a document. */
export const DEFAULT_MARGINS: Margins = { top: 48, right: 48, bottom: 48, left: 48 };

/** Paper size, margins and pattern language, stored once per pattern and shared by every page. */
export type PageSetup = {
    paperSize: PaperSize;
    margins: Margins;
    language: PatternLanguage;
    /** Running header and footer. Absent = none; optional on v3, kept by older v3 apps through
     *  spreads of `setup`. */
    headerFooter?: HeaderFooter;
};

/** Whether `margins` leave some room on `paperSize`: left + right under its width and top + bottom
 *  under its height. A pattern whose margins fail this for its own paper cannot be loaded, so a
 *  paper switch must check it before it saves. */
export function marginsFit(paperSize: PaperSize, margins: Margins): boolean {
    const { width, height } = PAPER_DIMENSIONS[paperSize];
    return margins.left + margins.right < width && margins.top + margins.bottom < height;
}

export function pageDimensions(setup: PageSetup): { width: number; height: number } {
    return PAPER_DIMENSIONS[setup.paperSize];
}

/** Regions whose default paper is Letter; everywhere else uses A4. */
const LETTER_REGIONS: ReadonlySet<string> = new Set(['US', 'CA', 'MX', 'PH', 'CL', 'CO', 'VE']);

/** The paper a new pattern starts on for `locale`. A locale that cannot be parsed
 *  falls back to Letter, the app's long-standing default. */
export function defaultPaperSize(locale: string): PaperSize {
    try {
        const region = new Intl.Locale(locale).maximize().region;
        return region !== undefined && LETTER_REGIONS.has(region) ? 'letter' : 'a4';
    } catch {
        return 'letter';
    }
}
